import React, { useState, useEffect } from "react";
import { collection, getDocs, doc, deleteDoc } from "firebase/firestore";
import { db } from "../utils/firebase";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";
import Paper from "@mui/material/Paper";
import Box from "@mui/material/Box";
import LinearProgress from "@mui/material/LinearProgress";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import { Search, Plus, Edit, Trash2, Copy, HelpCircle, Zap } from "lucide-react";
import ModalPortal from "../utils/ModalPortal";
import AdminDocumentTypeEditor from "./AdminDocumentTypeEditor";
import QuickAddDialog from "./QuickAddDialog";
import { useAdminSessionState } from './useAdminSessionState';
import { sortByDisplayLabel } from './sortByDisplayLabel';

// Category definitions
const CATEGORIES = [
  { value: 1, label: "Full Note" },
  { value: 2, label: "Upload" },
  { value: 3, label: "History" },
];

// Status options
const STATUSES = [
  { value: "draft", label: "Draft" },
  { value: "published", label: "Published" },
];

// Help content for Document Types
const DocumentTypesHelpContent = () => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
    <Box>
      <h4 style={{ margin: '0 0 8px 0' }}>Overview</h4>
      <p style={{ margin: 0, color: 'var(--color-text-secondary)' }}>
        Document Types define complete document configurations - combining steps (AI processing) 
        and sections (editor layout) into a cohesive document workflow.
      </p>
    </Box>

    <Box>
      <h4 style={{ margin: '0 0 8px 0' }}>Categories</h4>
      <Box sx={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)' }}>
        <p><strong>Full Note (1)</strong> - Complete clinical documentation with AI processing</p>
        <p><strong>Upload (2)</strong> - Document import/OCR workflows</p>
        <p><strong>History (3)</strong> - Treatment summaries pulling from patient history</p>
      </Box>
    </Box>

    <Box>
      <h4 style={{ margin: '0 0 8px 0' }}>Step Workflow</h4>
      <p style={{ margin: 0, color: 'var(--color-text-secondary)' }}>
        The ordered list of processing steps. Drag to reorder. Steps run in sequence during 
        document generation.
      </p>
    </Box>

    <Box>
      <h4 style={{ margin: '0 0 8px 0' }}>Section Layout</h4>
      <p style={{ margin: 0, color: 'var(--color-text-secondary)' }}>
        The ordered list of sections displayed in the document editor. Drag to reorder.
        Sections display data generated by steps.
      </p>
    </Box>

    <Box sx={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)', borderTop: '1px solid var(--color-border)', pt: 2 }}>
      Full documentation: <code>docs/admin-document-builder.md</code>
    </Box>
  </Box>
);

const AdminDocumentTypesEditor = ({ userId, onDirtyChange }) => {
  const [documentTypes, setDocumentTypes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useAdminSessionState(`admin:${userId}:documentTypesSearch`, "");
  const [categoryFilter, setCategoryFilter] = useAdminSessionState(`admin:${userId}:documentTypesCategory`, "all");
  const [statusFilter, setStatusFilter] = useAdminSessionState(`admin:${userId}:documentTypesStatus`, "all");
  const [sortBy, setSortBy] = useAdminSessionState(`admin:${userId}:documentTypesSort`, "name");
  const [sortDirection, setSortDirection] = useAdminSessionState(`admin:${userId}:documentTypesDirection`, "asc");

  // View state: "list" or "editor"
  const [view, setView] = useState("list");
  const [editingType, setEditingType] = useState(null);
  const [isNewType, setIsNewType] = useState(false);

  // Dialog states
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [typeToDelete, setTypeToDelete] = useState(null);
  const [helpDialogOpen, setHelpDialogOpen] = useState(false);
  const [quickAddDialogOpen, setQuickAddDialogOpen] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const typesSnapshot = await getDocs(collection(db, "documentTypes"));
      const typesData = typesSnapshot.docs.map(docSnap => ({
        id: docSnap.id,
        ...docSnap.data()
      }));
      setDocumentTypes(typesData);
    } catch (error) {
      console.error("Error fetching data:", error);
    }
    setIsLoading(false);
  };

  const handleNew = () => {
    setEditingType(null);
    setIsNewType(true);
    setView("editor");
  };

  const handleEdit = (docType) => {
    setEditingType(docType);
    setIsNewType(false);
    setView("editor");
  };

  const handleClone = (docType) => {
    // Create a clone with modified name
    const clonedType = {
      ...docType,
      id: "",
      name: `${docType.name} (Copy)`,
      status: "draft",
    };
    setEditingType(clonedType);
    setIsNewType(true);
    setView("editor");
  };

  const handleBack = () => {
    setView("list");
    setEditingType(null);
    setIsNewType(false);
  };

  const handleSaved = (savedType) => {
    if (savedType) {
      setEditingType(savedType);
      setIsNewType(false);
    }
    fetchData(); // Refresh list after save
  };

  const handleDelete = (docType) => {
    setTypeToDelete(docType);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!typeToDelete) return;
    try {
      await deleteDoc(doc(db, "documentTypes", typeToDelete.id));
      setDocumentTypes(prev => prev.filter(t => t.id !== typeToDelete.id));
      setDeleteDialogOpen(false);
      setTypeToDelete(null);
    } catch (error) {
      console.error("Error deleting document type:", error);
    }
  };

  // Filtering and Sorting
  const filteredTypes = documentTypes
    .filter(docType => {
      const matchesSearch = !searchQuery || 
        docType.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        docType.id?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        docType.description?.toLowerCase().includes(searchQuery.toLowerCase());
      
      const matchesCategory = categoryFilter === "all" || docType.category === Number(categoryFilter);
      const matchesStatus = statusFilter === "all" || docType.status === statusFilter;

      return matchesSearch && matchesCategory && matchesStatus;
    })
    .sort((a, b) => {
      let aVal, bVal;
      switch (sortBy) {
        case "name":
          aVal = a.name?.toLowerCase() || "";
          bVal = b.name?.toLowerCase() || "";
          break;
        case "category":
          aVal = a.category || 0;
          bVal = b.category || 0;
          break;
        case "format":
          aVal = a.format?.toLowerCase() || "";
          bVal = b.format?.toLowerCase() || "";
          break;
        case "status":
          aVal = a.status || "draft";
          bVal = b.status || "draft";
          break;
        case "steps":
          aVal = a.stepWorkflow?.length || 0;
          bVal = b.stepWorkflow?.length || 0;
          break;
        case "sections":
          aVal = a.sectionLayout?.length || 0;
          bVal = b.sectionLayout?.length || 0;
          break;
        case "updatedAt":
          aVal = a.updatedAt?.toDate?.() || a.updatedAt || new Date(0);
          bVal = b.updatedAt?.toDate?.() || b.updatedAt || new Date(0);
          break;
        default:
          aVal = a.name?.toLowerCase() || "";
          bVal = b.name?.toLowerCase() || "";
      }
      if (aVal < bVal) return sortDirection === "asc" ? -1 : 1;
      if (aVal > bVal) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortBy(column);
      setSortDirection("asc");
    }
  };

  if (isLoading) {
    return <LinearProgress />;
  }

  // Show editor view
  if (view === "editor") {
    return (
      <AdminDocumentTypeEditor
        onDirtyChange={onDirtyChange}
        documentType={editingType}
        isNew={isNewType}
        onBack={handleBack}
        onSave={handleSaved}
      />
    );
  }

  // Show list view
  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
          <Box sx={{ position: "relative" }}>
            <Search size={16} style={{ position: "absolute", left: 8, top: "50%", transform: "translateY(-50%)", color: "var(--color-text-secondary)" }} />
            <TextField
              size="small"
              placeholder="Search document types..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              sx={{ pl: 4, minWidth: 250 }}
              InputProps={{ sx: { pl: 4 } }}
            />
          </Box>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Category</InputLabel>
            <Select
              value={categoryFilter}
              label="Category"
              onChange={(e) => setCategoryFilter(e.target.value)}
            >
              <MenuItem value="all">All</MenuItem>
              {sortByDisplayLabel(CATEGORIES, category => category.label).map(cat => (
                <MenuItem key={cat.value} value={cat.value}>{cat.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 100 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={statusFilter}
              label="Status"
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <MenuItem value="all">All</MenuItem>
              {STATUSES.map(status => (
                <MenuItem key={status.value} value={status.value}>{status.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          <IconButton onClick={() => setHelpDialogOpen(true)}>
            <HelpCircle size={20} />
          </IconButton>
          <Button variant="outlined" startIcon={<Zap size={16} />} onClick={() => setQuickAddDialogOpen(true)}>
            Quick Add
          </Button>
          <Button variant="contained" startIcon={<Plus size={16} />} onClick={handleNew}>
            New Document
          </Button>
        </Box>
      </Box>

      {/* Table */}
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>
                <TableSortLabel
                  active={sortBy === "name"}
                  direction={sortBy === "name" ? sortDirection : "asc"}
                  onClick={() => handleSort("name")}
                >
                  Name
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortBy === "category"}
                  direction={sortBy === "category" ? sortDirection : "asc"}
                  onClick={() => handleSort("category")}
                >
                  Category
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortBy === "format"}
                  direction={sortBy === "format" ? sortDirection : "asc"}
                  onClick={() => handleSort("format")}
                >
                  Format
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortBy === "status"}
                  direction={sortBy === "status" ? sortDirection : "asc"}
                  onClick={() => handleSort("status")}
                >
                  Status
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortBy === "steps"}
                  direction={sortBy === "steps" ? sortDirection : "asc"}
                  onClick={() => handleSort("steps")}
                >
                  Steps
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortBy === "sections"}
                  direction={sortBy === "sections" ? sortDirection : "asc"}
                  onClick={() => handleSort("sections")}
                >
                  Sections
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortBy === "updatedAt"}
                  direction={sortBy === "updatedAt" ? sortDirection : "asc"}
                  onClick={() => handleSort("updatedAt")}
                >
                  Last Updated
                </TableSortLabel>
              </TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredTypes.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">
                    {documentTypes.length === 0 
                      ? "No document types found. Create one to get started."
                      : "No document types match your filters."}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredTypes.map(docType => (
                <TableRow key={docType.id} hover>
                  <TableCell>
                    <Typography 
                      variant="body2" 
                      fontWeight={500}
                      sx={{ cursor: 'pointer', color: 'var(--color-primary)' }}
                      onClick={() => handleEdit(docType)}
                    >
                      {docType.name}
                    </Typography>
                    {docType.description && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                        {docType.description}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={CATEGORIES.find(c => c.value === docType.category)?.label || docType.category}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip label={docType.format} size="small" variant="outlined" />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={docType.status || "draft"}
                      size="small"
                      color={docType.status === "published" ? "success" : "default"}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {docType.stepWorkflow?.length || 0}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {docType.sectionLayout?.length || 0}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {docType.updatedAt?.toDate?.()?.toLocaleDateString() || docType.updatedAt?.toLocaleDateString?.() || "—"}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <IconButton size="small" onClick={() => handleEdit(docType)} title="Edit">
                      <Edit size={16} />
                    </IconButton>
                    <IconButton size="small" onClick={() => handleClone(docType)} title="Clone">
                      <Copy size={16} />
                    </IconButton>
                    <IconButton size="small" onClick={() => handleDelete(docType)} title="Delete">
                      <Trash2 size={16} />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Delete Confirmation Dialog */}
      <ModalPortal isOpen={deleteDialogOpen}>
        <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
          <DialogTitle>Delete Document Type</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete "{typeToDelete?.name}"? This action cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
            <Button color="error" variant="contained" onClick={confirmDelete}>
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      </ModalPortal>

      {/* Help Dialog */}
      <ModalPortal isOpen={helpDialogOpen}>
        <Dialog open={helpDialogOpen} onClose={() => setHelpDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Document Types Help</DialogTitle>
          <DialogContent>
            <DocumentTypesHelpContent />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setHelpDialogOpen(false)}>Close</Button>
          </DialogActions>
        </Dialog>
      </ModalPortal>

      {/* Quick Add Dialog */}
      <QuickAddDialog
        open={quickAddDialogOpen}
        onClose={() => setQuickAddDialogOpen(false)}
        onSave={() => setQuickAddDialogOpen(false)}
      />
    </Box>
  );
};

export default AdminDocumentTypesEditor;
